import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { getErrorMessage } from '../../../admin/containers/adsMeta/utils';
import { IAssignManagedRequest, IMetaConnectionState } from './types';
import { META_CONNECTION_QUERY_KEY } from './useMetaConnectionQuery';
import { META_VISIBLE_ASSETS_QUERY_KEY } from './useMetaVisibleAssetsQuery';

// `ManagedConnectionResponse` per the API doc — the subset returned by assign /
// unassign. Other fields on IMetaConnectionState (adAccountIds,
// lastMetricsSyncAt, lastMetricsSyncError) only come from the GET endpoint, so
// we merge the response into the cached value rather than replacing it.
type ManagedConnectionResponse = Pick<
	IMetaConnectionState,
	'levitateAccountId' | 'managedAdAccountIds' | 'hasCustomerToken'
>;

// POST admin/ads/meta/connections/{levitateAccountId}/managed
// This REPLACES the customer's full managed list. Callers must read the current
// list (via useMetaConnectionQuery) and submit the desired full list, not a delta.
export function useMetaAssignManagedMutation() {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({ levitateAccountId, metaAdAccountIds }: IAssignManagedRequest) =>
			userSession.webServiceHelper.callAsync<ManagedConnectionResponse>(
				`admin/ads/meta/connections/${encodeURIComponent(levitateAccountId)}/managed`,
				'POST',
				{ metaAdAccountIds }
			),
		onSuccess: (data, variables) => {
			// Merge — keep any optional fields the GET endpoint had populated.
			reactQueryClient.setQueryData<IMetaConnectionState | undefined>(
				[META_CONNECTION_QUERY_KEY, variables.levitateAccountId],
				prev => (prev ? { ...prev, ...data } : data)
			);
			reactQueryClient.invalidateQueries({ queryKey: [META_VISIBLE_ASSETS_QUERY_KEY] });
		},
		// Mutations don't flow through QueryCache.onError — surface failures
		// explicitly so callers that don't render `mutation.error` inline still
		// get user-visible feedback.
		onError: error => {
			toast.error(getErrorMessage(error) || "Couldn't assign the managed ad account.");
		},
	});
}
